import React, { useEffect, useState } from "react";
import orendaService from "../../services/orenda.service";
import { parseLegalDocumentsResponse } from "./orendaApplicationHelpers";
import { orendaUserManager } from "./orendaUserManager";
import { orendaOidcConfig } from "../../config/orendaOidcConfig";

// LEGAL_AGREEMENTS step. Rendered inside OrendaShell (see StepRouter in
// OrendaLanding). Fetches the current documents, lets the applicant tick
// each one, then submits acceptance.
const OrendaLegalAgreements = ({ application, onAdvance }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [docsBundle, setDocsBundle] = useState(null); // { documents, currentAcceptance }
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let alive = true;
    orendaService
      .getLegalDocumentOrenda()
      .then((response) => {
        if (!alive) return;
        setDocsBundle(parseLegalDocumentsResponse(response));
        setLoading(false);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const docs = docsBundle?.documents?.docs || [];
  const requiredIds = docs.filter((d) => d.required).map((d) => d.id);
  const allRequiredChecked = requiredIds.every((id) => checked[id]);

  const toggle = (docId) =>
    setChecked((prev) => ({ ...prev, [docId]: !prev[docId] }));

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setSessionExpired(false);

    const accepted = docs
      .filter((d) => checked[d.id])
      .map((d) => ({ id: d.id, hash: d.hash, version: d.version }));

    try {
      await orendaService.acceptLegalDocumentOrenda({
        documentSetVersion: docsBundle?.documents?.documentSetVersion,
        applicationId: application?.applicationId,
        acceptedDocuments: accepted,
      });
      onAdvance?.();
    } catch (err) {
      if (err?.code === "SSO_SESSION_EXPIRED") {
        setSessionExpired(true);
      } else {
        setSubmitError(
          err?.response?.data?.errorMessage || err?.message || String(err)
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reAuthenticate = () => {
    orendaUserManager.signinRedirect({
      extraQueryParams: {
        identity_provider: orendaOidcConfig.identity_provider,
        prompt: "login",
      },
    });
  };

  if (loading) {
    return <div className="orenda-card">Loading legal documents…</div>;
  }
  if (loadError) {
    return (
      <div className="orenda-alert orenda-alert--warn">
        <strong>Could not load legal documents.</strong>
        <div>{loadError}</div>
      </div>
    );
  }

  return (
    <>
      <h2 className="orenda-heading">Review and accept legal agreements</h2>
      <p className="orenda-subheading">
        Please review each required document. Check the box to acknowledge you
        have read it, then continue.
      </p>

      <div className="orenda-card">
        {docs.map((d) => (
          <div key={d.id} className="orenda-doc-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="orenda-doc-title">
                {d.name}
                {d.required && (
                  <span className="orenda-doc-pill">required</span>
                )}
              </div>
              <div className="orenda-doc-meta">
                v{d.version} · effective {d.effectiveDate}
              </div>
              <label className="orenda-inline-check">
                <input
                  type="checkbox"
                  checked={!!checked[d.id]}
                  onChange={() => toggle(d.id)}
                />
                I have read and accept the {d.name}
              </label>
            </div>
            <a
              href={d.location}
              target="_blank"
              rel="noopener noreferrer"
              className="orenda-btn-link"
            >
              Open PDF ↗
            </a>
          </div>
        ))}
      </div>

      {sessionExpired && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Your session expired.</strong>
          <div style={{ marginTop: 4, marginBottom: 8 }}>
            Sign in again to accept the documents.
          </div>
          <button onClick={reAuthenticate} className="orenda-btn-primary">
            Sign in again
          </button>
        </div>
      )}

      {submitError && (
        <div className="orenda-alert orenda-alert--warn">
          <strong>Submission failed.</strong>
          <div>{submitError}</div>
        </div>
      )}

      <div className="orenda-actions">
        <span style={{ color: "#6b6f78", fontSize: 13 }}>
          {allRequiredChecked
            ? "All required documents accepted."
            : "Tick every required document to continue."}
        </span>
        <button
          onClick={submit}
          disabled={!allRequiredChecked || submitting}
          className="orenda-btn-primary"
        >
          {submitting ? "Submitting…" : "Accept and continue"}
        </button>
      </div>
    </>
  );
};

export default OrendaLegalAgreements;
